import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import useFetchCart from './FetchCart';
import 'react-calendar/dist/Calendar.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import moment from 'moment';
import BASE_URL, { getImageURL } from '../config';

const ViewCart = () => {
  const { cart, loading, error } = useFetchCart();
  const navigate = useNavigate();
  const [deliveryOption, setDeliveryOption] = useState('storePickup');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [calendarVisible, setCalendarVisible] = useState(false);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const handleRemoveItem = (slug) => {
    // Update the cart by removing the item with the given slug
    const updatedCart = cart.items.filter(item => item.item.slug !== slug);
    // Note: The updateCart function is not available from useFetchCart
    // This is an example; actual implementation will vary
    // updateCart(updatedCart);
  };

  const handleUpdateQuantity = (slug, quantity) => {
    const updatedCart = cart.items.map(item => {
      if (item.item.slug === slug) {
        return { ...item, quantity: Math.max(quantity, 1) }; // Ensure quantity is at least 1
      }
      return item;
    });
    // Note: The updateCart function is not available from useFetchCart
    // This is an example; actual implementation will vary
    // updateCart(updatedCart);
  };

  const totalItems = cart.items.reduce((total, item) => total + item.quantity, 0);
  const totalPrice = cart.items.reduce((total, item) => total + item.item.price * item.quantity, 0).toFixed(2);

  const handleCheckout = () => {
    // Handle checkout logic here, e.g., saving the selected options and navigating to the checkout page
    navigate('/checkout');
  };

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  };

  const isValidDate = (date) => {
    if (!date) return false;
    const today = new Date();
    const in30Days = new Date(today);
    in30Days.setDate(today.getDate() + 30);

    return date >= today && date <= in30Days && !isWeekend(date);
  };

  const handleDateChange = (date) => {
    if (isValidDate(date)) {
      setSelectedDate(date);
      setCalendarVisible(false);
    } else {
      alert('Please select a valid date within the next 30 days, excluding weekends.');
    }
  };

  const minDate = new Date();
  minDate.setHours(9, 0, 0, 0); // Set min time to 9 AM today

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30); // Set max date to 30 days from today

  const generateTimeOptions = () => {
    const options = [];
    for (let i = 9; i <= 17; i++) {
      options.push(`${i}:00`);
    }
    return options;
  };

  const handleTimeChange = (event) => {
    setSelectedTime(event.target.value);
  };

  return (
    <div className="container">
      <h1>Your Cart</h1>
      {cart.items.length > 0 ? (
        <>
          <ul className="list-group">
            {cart.items.map(cartItem => (
              <li key={cartItem.item.slug} className="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <img
                    src={getImageURL(cartItem.item.mainimage)}
                    alt={cartItem.item.title}
                    className="img-thumbnail"
                    style={{ width: '100px' }}
                  />
                  <h5>{cartItem.item.title}</h5>
                  <p>Price: CHF {cartItem.item.price}</p>
                  <div className="d-flex align-items-center">
                    <button className="btn btn-sm btn-secondary mx-2" onClick={() => handleUpdateQuantity(cartItem.item.slug, cartItem.quantity - 1)}>-</button>
                    <span>{cartItem.quantity}</span>
                    <button className="btn btn-sm btn-secondary mx-2" onClick={() => handleUpdateQuantity(cartItem.item.slug, cartItem.quantity + 1)}>+</button>
                  </div>
                </div>
                <button className="btn btn-danger" onClick={() => handleRemoveItem(cartItem.item.slug)}>Remove</button>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <h4>Total Items: {totalItems}</h4>
            <h4>Total Price: CHF {totalPrice}</h4>
          </div>
          <div className="form-group mt-3">
            <label htmlFor="specialInstructions">Special ordering instructions</label>
            <textarea
              id="specialInstructions"
              className="form-control"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
            />
          </div>
          <div className="mt-3">
            <h5>Delivery Option</h5>
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn ${deliveryOption === 'storePickup' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDeliveryOption('storePickup')}
              >
                Store Pickup
              </button>
              <button
                type="button"
                className={`btn ${deliveryOption === 'homeDelivery' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDeliveryOption('homeDelivery')}
              >
                Home Delivery
              </button>
            </div>
          </div>
          <div className="mt-3">
            <h5>Choose a delivery date</h5>
            <div className="form-control" onClick={() => setCalendarVisible(!calendarVisible)}>
              {selectedDate ? moment(selectedDate).format('ddd, D MMMM YYYY') : 'Select a date'}
            </div>
            {calendarVisible && (
              <Calendar
                onChange={handleDateChange}
                value={selectedDate}
                minDate={minDate}
                maxDate={maxDate}
                tileDisabled={({ date }) => isWeekend(date)}
              />
            )}
          </div>
          {selectedDate && (
            <div className="mt-3">
              <h5>Choose a delivery time</h5>
              <select
                className="form-control"
                value={selectedTime}
                onChange={handleTimeChange}
              >
                <option value="">Select a time</option>
                {generateTimeOptions().map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          )}
          <button className="btn btn-primary mt-3" onClick={handleCheckout}>Checkout</button>
        </>
      ) : (
        <p>Your cart is empty.</p>
      )}
    </div>
  );
};

export default ViewCart;
